// Phase-1 Touchpoints — Account detail tab.
//
// List + actions container surfaced under the Account detail's
// "Touchpoints" tab. Uses the manual Account-touchpoint REST surface
// (BE Phase-1) and runs all writes through the React-Query mutation
// hooks so the list invalidates after every action.
//
// When ``?touchpoint=:id`` is present in the URL, the tab swaps the
// list for ``TouchpointDetailPanel`` so the detail surface lives at
// the same route the list does — emails / agent output deep-link
// to ``/dashboard/accounts/:id?tab=touchpoints&touchpoint=:tpId``.

import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { Button } from "@/design-system";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useAccountDetailQuery } from "@/features/accounts/hooks";

import { TouchpointFormModal } from "./TouchpointFormModal";
import { TouchpointDetailPanel } from "./TouchpointDetailPanel";
import {
  useAccountTouchpointsQuery,
  useCancelTouchpointMutation,
  useCompleteTouchpointMutation,
  useCreateAccountTouchpointMutation,
  useDeleteTouchpointMutation,
  useUpdateTouchpointMutation,
} from "./hooks";
import type {
  Touchpoint,
  TouchpointCreateInput,
  TouchpointKind,
  TouchpointStatus,
} from "./types";
import { TOUCHPOINT_KIND_LABEL, TOUCHPOINT_STATUS_LABEL } from "./types";


export interface AccountTouchpointsTabProps {
  accountId: string;
}


export function AccountTouchpointsTab({
  accountId,
}: AccountTouchpointsTabProps) {
  const [search, setSearch] = useSearchParams();
  const focusedId = search.get("touchpoint") ?? null;
  const accountQuery = useAccountDetailQuery(accountId);
  const accountName =
    accountQuery.data?.legal_name
      ?? accountQuery.data?.dba_name
      ?? "Account";

  const setFocusedTouchpoint = (id: string | null) => {
    const sp = new URLSearchParams(search);
    if (id) sp.set("touchpoint", id);
    else sp.delete("touchpoint");
    setSearch(sp, { replace: false });
  };

  if (focusedId) {
    return (
      <TouchpointDetailPanel
        accountId={accountId}
        accountName={accountName}
        touchpointId={focusedId}
        onBack={() => setFocusedTouchpoint(null)}
      />
    );
  }

  return (
    <AccountTouchpointsList
      accountId={accountId}
      onOpenDetail={(id) => setFocusedTouchpoint(id)}
    />
  );
}


function AccountTouchpointsList({
  accountId, onOpenDetail,
}: {
  accountId: string;
  onOpenDetail: (touchpointId: string) => void;
}) {
  const listQuery = useAccountTouchpointsQuery(accountId);
  const createMutation = useCreateAccountTouchpointMutation(accountId);
  const updateMutation = useUpdateTouchpointMutation();
  const completeMutation = useCompleteTouchpointMutation();
  const cancelMutation = useCancelTouchpointMutation();
  const deleteMutation = useDeleteTouchpointMutation();

  const [modalState, setModalState] = useState<
    | { mode: "create" }
    | { mode: "edit"; touchpoint: Touchpoint }
    | null
  >(null);
  const [modalError, setModalError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<Touchpoint | null>(null);

  const rows: Touchpoint[] = useMemo(
    () => listQuery.data?.rows ?? [],
    [listQuery.data],
  );

  const onAdd = () => {
    setModalError(null);
    setModalState({ mode: "create" });
  };

  const onEdit = (tp: Touchpoint) => {
    setModalError(null);
    setModalState({ mode: "edit", touchpoint: tp });
  };

  const onSubmit = (values: TouchpointCreateInput) => {
    setModalError(null);
    if (modalState?.mode === "edit") {
      updateMutation.mutate(
        {
          touchpoint_id: modalState.touchpoint.touchpoint_id,
          account_id: accountId,
          values,
        },
        {
          onSuccess: () => setModalState(null),
          onError: (err) => setModalError(describeError(err)),
        },
      );
      return;
    }
    createMutation.mutate(values, {
      onSuccess: () => setModalState(null),
      onError: (err) => setModalError(describeError(err)),
    });
  };

  const onComplete = (tp: Touchpoint) => {
    completeMutation.mutate({
      touchpoint_id: tp.touchpoint_id, account_id: accountId,
    });
  };

  const onCancel = (tp: Touchpoint) => {
    cancelMutation.mutate({
      touchpoint_id: tp.touchpoint_id, account_id: accountId,
    });
  };

  const onConfirmRemove = () => {
    if (!removing) return;
    deleteMutation.mutate(
      { touchpoint_id: removing.touchpoint_id, account_id: accountId },
      { onSettled: () => setRemoving(null) },
    );
  };

  return (
    <section
      data-testid="account-touchpoints-tab"
      className="flex flex-col gap-3 rounded-md border border-border-subtle bg-canvas/40 p-4"
    >
      <header className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-text-primary">
            Touchpoints
          </h2>
          <p className="mt-1 max-w-xl text-xs text-text-secondary">
            Meetings, calls, emails, and other interactions you have with
            this Account.
          </p>
        </div>
        <Button
          variant="primary"
          data-testid="account-touchpoints-add"
          onClick={onAdd}
        >
          + Log Touchpoint
        </Button>
      </header>

      {listQuery.isPending ? (
        <p
          data-testid="account-touchpoints-loading"
          className="px-3 py-4 text-xs text-text-tertiary"
        >
          Loading…
        </p>
      ) : listQuery.isError ? (
        <p
          data-testid="account-touchpoints-error"
          className="px-3 py-4 text-xs text-ember-ink"
        >
          Couldn&rsquo;t load Touchpoints. Try refreshing.
        </p>
      ) : rows.length === 0 ? (
        <p
          data-testid="account-touchpoints-empty"
          className="rounded-md border border-dashed border-border-subtle px-3 py-6 text-center text-xs text-text-tertiary"
        >
          Log meetings, calls, emails, and other Account interactions.
        </p>
      ) : (
        <ul
          role="list"
          data-testid="account-touchpoints-list"
          className="flex flex-col gap-2"
        >
          {rows.map((tp) => (
            <TouchpointRow
              key={tp.touchpoint_id}
              row={tp}
              onOpenDetail={() => onOpenDetail(tp.touchpoint_id)}
              onEdit={() => onEdit(tp)}
              onComplete={() => onComplete(tp)}
              onCancel={() => onCancel(tp)}
              onRemove={() => setRemoving(tp)}
              pending={
                updateMutation.isPending
                  || completeMutation.isPending
                  || cancelMutation.isPending
                  || deleteMutation.isPending
              }
            />
          ))}
        </ul>
      )}

      <TouchpointFormModal
        open={modalState !== null}
        mode={modalState?.mode ?? "create"}
        touchpoint={
          modalState?.mode === "edit" ? modalState.touchpoint : null
        }
        saving={createMutation.isPending || updateMutation.isPending}
        error={modalError}
        onClose={() => setModalState(null)}
        onSubmit={onSubmit}
      />

      <ConfirmDialog
        open={removing !== null}
        title="Delete this Touchpoint?"
        body="This soft-deletes the row. The history stays on the audit log."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={onConfirmRemove}
        onCancel={() => setRemoving(null)}
        testId="account-touchpoints-remove-confirm"
      />
    </section>
  );
}


function TouchpointRow({
  row, onOpenDetail, onEdit, onComplete, onCancel, onRemove, pending,
}: {
  row: Touchpoint;
  onOpenDetail: () => void;
  onEdit: () => void;
  onComplete: () => void;
  onCancel: () => void;
  onRemove: () => void;
  pending: boolean;
}) {
  const when = displayDate(row.scheduled_at ?? row.occurred_at);
  return (
    <li
      data-testid={`account-touchpoint-row-${row.touchpoint_id}`}
      className="flex items-start justify-between gap-3 rounded-md border border-border-subtle bg-canvas/60 px-3 py-2"
    >
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <KindIcon kind={row.kind} />
          <button
            type="button"
            data-testid={`account-touchpoint-open-${row.touchpoint_id}`}
            onClick={onOpenDetail}
            className="text-left text-sm font-medium text-text-primary hover:text-teal hover:underline"
          >
            {row.subject || TOUCHPOINT_KIND_LABEL[row.kind]}
          </button>
          <StatusBadge status={row.status} />
        </div>
        <span className="text-[11px] text-text-tertiary">
          {when}
          {row.duration_minutes != null
            ? ` · ${row.duration_minutes} min`
            : ""}
          {row.participants?.length
            ? ` · ${row.participants.join(", ")}`
            : ""}
        </span>
        {row.body && (
          <p className="text-xs text-text-secondary line-clamp-2">
            {row.body}
          </p>
        )}
        {row.outcome_summary && (
          <p
            data-testid={`account-touchpoint-outcome-${row.touchpoint_id}`}
            className="text-xs text-text-secondary"
          >
            <span className="font-semibold">Outcome:</span> {row.outcome_summary}
          </p>
        )}
      </div>

      <div className="flex flex-shrink-0 items-center gap-2 text-xs">
        {row.status === "scheduled" && (
          <>
            <button
              type="button"
              data-testid={`account-touchpoint-complete-${row.touchpoint_id}`}
              onClick={onComplete}
              disabled={pending}
              className="font-medium text-teal hover:underline disabled:opacity-50"
            >
              Complete
            </button>
            <button
              type="button"
              data-testid={`account-touchpoint-cancel-${row.touchpoint_id}`}
              onClick={onCancel}
              disabled={pending}
              className="font-medium text-text-secondary hover:underline disabled:opacity-50"
            >
              Cancel
            </button>
          </>
        )}
        <button
          type="button"
          data-testid={`account-touchpoint-edit-${row.touchpoint_id}`}
          onClick={onEdit}
          disabled={pending}
          className="font-medium text-text-secondary hover:underline disabled:opacity-50"
        >
          Edit
        </button>
        <button
          type="button"
          data-testid={`account-touchpoint-remove-${row.touchpoint_id}`}
          onClick={onRemove}
          disabled={pending}
          className="font-medium text-ember-ink hover:underline disabled:opacity-50"
        >
          Delete
        </button>
      </div>
    </li>
  );
}


function KindIcon({ kind }: { kind: TouchpointKind }) {
  // Single-character glyphs keep the row dense without pulling in a
  // new icon dependency. The visible label still carries semantics.
  const glyph = kind === "meeting"
    ? "MTG"
    : kind === "call"
      ? "CALL"
      : kind === "email"
        ? "EMAIL"
        : "NOTE";
  return (
    <span
      aria-hidden="true"
      className="inline-flex items-center rounded-full bg-recessed px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-text-tertiary"
    >
      {glyph}
    </span>
  );
}


function StatusBadge({ status }: { status: TouchpointStatus }) {
  const cls =
    status === "completed"
      ? "bg-sage-tint text-teal"
      : status === "cancelled"
        ? "bg-recessed text-text-tertiary"
        : status === "no_show"
          ? "bg-ember-tint text-ember-ink"
          : "bg-gold-tint text-gold-ink";
  return (
    <span
      data-testid="account-touchpoint-status-badge"
      className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${cls}`}
    >
      {TOUCHPOINT_STATUS_LABEL[status]}
    </span>
  );
}


function displayDate(iso: string | null): string {
  if (!iso) return "Unscheduled";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Unscheduled";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}


function describeError(err: unknown): string {
  if (err instanceof Error) return err.message;
  return "Couldn't save the Touchpoint. Please try again.";
}
